/**
 * The choropleth's data hook (VERA-260) — per-country aggregates for the map's third
 * render mode.
 *
 * Mirrors `useAnalyticsTopInfluencers` down to the comments it inherits: the page-global
 * `area` and `scan_statuses` filters are merged HERE rather than at the call site, so a
 * card cannot be built that quietly ignores them (§42's referrer prop reached 2 of 37
 * call sites as an opt-in; making it the hook's job is what stops that recurring), and
 * the whole request is the query key so every filter invalidates the cache by
 * construction.
 *
 * `scan_statuses` is legitimate here for the structural reason `useAnalyticsScanStatusParam`
 * states: the filter resolves from `scans.invalid` / `.tampered`, so it needs an endpoint
 * that reads the `scans` table rather than `certificate_action_logs`. `/analytics/admin/
 * countries` does. It is now the third such endpoint, not an exception to the rule.
 *
 * ⚠️ There IS a real interaction between that filter and the invalid-rate metric, and it
 * is the consumer's to explain, not this hook's to prevent: filtering the page to
 * "Invalid" scans makes every country's invalid rate 100% by construction, because the
 * numerator and the denominator become the same population. The map would be correct and
 * useless. See `ScansByCountry` for how the card handles it.
 */
import { useQuery } from '@tanstack/react-query'

import { analyticsCountryMetricsApi, type CountryMetricsParams } from 'services'

import {
  useAnalyticsAreaParam,
  useAnalyticsScanStatusParam,
} from './useAnalyticsAreaParam'

/** Everything the caller supplies. The two page-global filters are added below. */
export type UseAnalyticsCountryMetricsParams = Omit<
  CountryMetricsParams,
  'area' | 'scan_statuses'
>

export const useAnalyticsCountryMetrics = (
  params: UseAnalyticsCountryMetricsParams,
  options?: { enabled?: boolean },
) => {
  const area = useAnalyticsAreaParam()
  const scanStatuses = useAnalyticsScanStatusParam()

  const request: CountryMetricsParams = {
    ...params,
    ...(scanStatuses ? { scan_statuses: scanStatuses } : {}),
    ...(area ? { area } : {}),
  }

  return useQuery({
    queryKey: ['analytics', 'country-metrics', request],
    queryFn: () => analyticsCountryMetricsApi.get(request),
    enabled: options?.enabled ?? true,
    /*
      Keep the previous countries painted while a new set loads. §19's `isLoading` vs
      `isFetching` split — and it matters more here than on a table: a choropleth that
      blanks to an empty world on every filter nudge reads as "you have no scans
      anywhere", which is a statement about the data rather than about the loading state.
    */
    placeholderData: previous => previous,
    staleTime: 60 * 1000,
  })
}
